import type { ImageModel } from 'ai';
import {
    BaseImageProvider,
    type ImageProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import { buildInputPayloadFromConfig, getWavespeedModelConfig } from './utils';
import { wavespeedStatusMapping } from './config';
import type { WavespeedCreateResponse, WavespeedTaskResponse } from './types';
import { getValueByPath, isStatusMatch } from '../tools';


// 创建提供商实例
const provider = new BaseImageProvider(
    {
        baseUrlEnv: 'WAVESPEED_BASE_URL',
        apiKeyEnv: 'WAVESPEED_API_KEY',
        debugEnv: 'WAVESPEED_DEBUG',
        providerName: 'WAVESPEED',
    },
    {
        timeoutMs: 10 * 60 * 1000,
        backoff: {
            initialDelayMs: 5000,
            maxSteps: 20,
            scale: 1,
            jitter: 0.2,
            maxDelayMs: 10000,
        },
    }
);

// 实现适配器
const adapter: ImageProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 获取服务商配置 (用于确定模型请求路径)
        const config = getWavespeedModelConfig(modelId);
        // 构建输入参数（可能是异步构建）
        const payload = await buildInputPayloadFromConfig(modelId, params);
        // 解析 API 路径（支持根据图像数量动态切换，如 edit / remix）
        const image_urls: string[] = (params.providerOptions as any)?.url?.image_input ?? [];
        const apiPath = typeof config.apiPath === 'function'
            ? config.apiPath({ hasImage: image_urls.length > 0, imageCount: image_urls.length })
            : config.apiPath;
        // 返回请求配置
        return {
            url: `${context.baseUrl}${apiPath}`,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
            body: payload,
        };
    },

// 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: WavespeedCreateResponse): CreateTaskResult {
        // 读取服务商配置 (用于确定 taskId 路径)
        const config = getWavespeedModelConfig(modelId) as any;
        const taskIdPath = wavespeedStatusMapping.taskIdPath;
        // 获取任务 ID
        const taskId = getValueByPath(data, taskIdPath) || (data as any).data?.id || (data as any).data?.taskId;
        if (!taskId) {
            throw new Error(`Failed to get task id from response`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // 获取模型配置 (用于确定轮询路径)
        const config = getWavespeedModelConfig(modelId);
        const pollApiPath = config.pollApiPath;
        // 返回请求配置
        return {
            url: (requestId) => `${context.baseUrl}${pollApiPath.replace('${requestId}', requestId)}`,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: WavespeedTaskResponse): PollTaskResult {
        // 读取服务商配置 (状态映射)
        const config = getWavespeedModelConfig(modelId) as any;
        const statusMapping = (config?.statusMapping) || wavespeedStatusMapping;
        // 获取任务状态
        const state = getValueByPath(data, statusMapping.statePath);
        // 如果状态值未找到则视为**处理中**
        if (state === undefined) {
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 1. 判断**成功**状态
        if (isStatusMatch(state, statusMapping.success)) {
            // 获取服务商配置 (用于确定 imageUrl 路径)
            const imageUrlPath = statusMapping.imageUrlPath;
            // 获取结果字符串 or 数组
            const urlValue = getValueByPath(data, imageUrlPath);
            // 结果不存在视为**失败**状态
            if (!urlValue) {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing resultJson in response',
                    raw: data,
                };
            }
            // 解析结果
            // 结果是字符串则解析为 JSON
            let imageUrls: string[] = [];
            if (typeof urlValue === 'string') {
                try {
                    const parsed = JSON.parse(urlValue);
                    if (parsed && Array.isArray(parsed.resultUrls)) {
                        if (parsed.resultUrls.length > 0 && typeof parsed.resultUrls[0] === 'object' && parsed.resultUrls[0].resultUrl) {
                            imageUrls = parsed.resultUrls.map((item: any) => item.resultUrl);
                        } else {
                            imageUrls = parsed.resultUrls;
                        }
                    } else if (Array.isArray(parsed)) {
                        imageUrls = parsed;
                    }
                } catch {
                    imageUrls = [urlValue];
                }
            // 结果是数组则解析为 URL 列表
            } else if (Array.isArray(urlValue)) {
                if (urlValue.length > 0 && typeof urlValue[0] === 'object' && (urlValue[0] as any).resultUrl) {
                    imageUrls = (urlValue as any[]).map((item: any) => item.resultUrl);
                } else {
                    imageUrls = urlValue;
                }
            } else if (urlValue && typeof urlValue === 'object') {
                if (Array.isArray((urlValue as any).resultUrls)) {
                    const resultUrls = (urlValue as any).resultUrls;
                    if (resultUrls.length > 0 && typeof resultUrls[0] === 'object' && resultUrls[0].resultUrl) {
                        imageUrls = resultUrls.map((item: any) => item.resultUrl);
                    } else {
                        imageUrls = resultUrls;
                    }
                } else if (Array.isArray((urlValue as any).urls)) {
                    imageUrls = (urlValue as any).urls;
                }
            }
            // 图像 URL 不存在则视为**失败**状态
            if (!imageUrls || imageUrls.length === 0) {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing image URLs in result',
                    raw: data,
                };
            }
            // 返回**成功**状态
            return {
                status: TaskStatus.SUCCESS,
                imageUrls: imageUrls,
                raw: data,
            };
        }

        // 2. 判断**失败**状态
        if (isStatusMatch(state, statusMapping.failed)) {
            const error = getValueByPath(data, statusMapping.errorPath);
            // 返回**失败**状态
            return {
                status: TaskStatus.FAILED,
                error: String(error) || 'unknown error',
                raw: data,
            };
        }

        // 3. 判断**处理中**状态
        if (isStatusMatch(state, statusMapping.processing)) {
            // 返回**处理中**状态
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 4. 未知状态：默认视为**处理中**
        // 返回**处理中**状态
        return {
            status: TaskStatus.PROCESSING,
            raw: data,
        };
    },
};

// 导出工厂函数
export const wavespeed = {
    image: (modelId: string): ImageModel => {
        const config = getWavespeedModelConfig(modelId);
        const timeoutMs = config?.timeoutMs;
        return provider.createImageModel(modelId, adapter, timeoutMs);
    },
};